// @flow
import { useState } from "react";

import { getDisplayName } from "../../react/utils";
import { MODULARUI_STATUS } from "../../constants/Constants";
import Href from "../../models/href/Href";

import type { ComponentType } from "react";

/**
 * Generate modular ui url
 */
const getUrl = (resource: string | Function | Href, props: Object): Href => {
  const url = typeof resource === "function" ? resource(props) : resource;

  if (url instanceof Href) {
    return url;
  }

  return new Href(url);
};

/**
 */
export const useUrl = (
  resource: string | Function | Href,
  props: Object,
): Href => {
  const [previousUrl, setPreviousUrl] = useState(null);
  const [previousExact, setPreviousExact] = useState(false);

  const url = getUrl(resource, props);

  if (url == null && previousUrl == null) {
    return new Href("");
  }

  const { match } = props;

  // when match is not available through props,
  // this is a direct call to the modular ui hoc, not using a route
  if (!match) {
    return url;
  }

  if (url.equalsWithParameters(previousUrl)) {
    return previousUrl || url;
  }

  // Always create an url when the match is exact
  // Or when the match is not exact AND the original match also was not exact
  // Or when the new start uri is different from the previous url --> different resource for same panel
  if (
    match.isExact === true ||
    previousExact === match.isExact ||
    !previousUrl?.startsWith(match.url)
  ) {
    setPreviousUrl(url);
    setPreviousExact(match.isExact);

    return url;
  }

  return previousUrl || new Href("");
};

/**
 * Create key for modularui request based on component name and requested url
 */
export const getKey = (
  WrappedComponent: ComponentType<any>,
  name: string,
  url: string,
): string => {
  if (url) {
    return `${getDisplayName(WrappedComponent, name)}(${url.split("?")[0]})`;
  }

  return getDisplayName(WrappedComponent, name);
};

/**
 * Check if the model corresponding to a modular ui service should be reloaded
 */
export const useReload = (modelEntry: Object, reload: number = 0): boolean => {
  if (reload > 0) {
    const isFullyLoaded = modelEntry?.status === MODULARUI_STATUS.FINISHED;
    const lastModification = modelEntry?.lastModification ?? 0;

    if (isFullyLoaded && lastModification < reload) {
      return true;
    }
  }

  return false;
};
